import {
  BadgeCheck,
  Building2,
  Globe,
  Landmark,
  Mail,
  Phone,
  ShieldCheck,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import type { UserProfile } from "@/interfaces/profile";
import { useSingleUser } from "@/hooks";
import MerchantStats from "./MerchantStats";

interface MerchantOverviewProps {
  merchantId: string;
}

export default function MerchantOverview({
  merchantId,
}: MerchantOverviewProps) {
  const { data: merchant } = useSingleUser(merchantId);

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <MerchantStats merchantId={merchantId} />

      {/* Verification */}

      <Card className="rounded-2xl">
        <div className="p-6">
          <h3 className="font-semibold text-lg">Verification</h3>

          <p className="text-sm text-muted-foreground mt-1 mb-6">
            Verification completed by this merchant.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <VerificationBadge
              icon={<BadgeCheck className="w-4 h-4" />}
              title="Government ID"
              active={merchant?.kycStatus ? true : false}
            />

            <VerificationBadge
              icon={<Phone className="w-4 h-4" />}
              title="Phone"
              active={merchant?.phoneVerified ?? false}
            />

            <VerificationBadge
              icon={<Mail className="w-4 h-4" />}
              title="Email"
              active={merchant?.emailVerified ?? false}
            />

            <VerificationBadge
              icon={<Building2 className="w-4 h-4" />}
              title="Merchant"
              active={merchant?.merchantName !== null}
            />

            <VerificationBadge
              icon={<Landmark className="w-4 h-4" />}
              title="Bank"
              active={true}
              // active={merchant?.paymentMethods?.length > 0}
            />

            <VerificationBadge
              icon={<ShieldCheck className="w-4 h-4" />}
              title="Face Verification"
              active={merchant?.emailVerified ?? false}
            />
          </div>
        </div>
      </Card>

      {/* Payment Methods */}

      <Card className="rounded-2xl">
        <div className="p-6">
          <h3 className="font-semibold text-lg">Payment Methods</h3>

          <p className="text-sm text-muted-foreground mt-1 mb-6">
            Payment methods accepted by this merchant.
          </p>

          <div className="flex flex-wrap gap-2">
            {merchant?.paymentMethods?.map((method: any) => (
              <Badge
                key={method.id}
                variant="secondary"
                className="rounded-full px-4 py-2"
              >
                {method.type}
              </Badge>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>

      <p className="font-semibold mt-1">{value}</p>
    </div>
  );
}

function VerificationBadge({
  title,
  icon,
  active,
}: {
  title: string;
  icon: React.ReactNode;
  active: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 flex items-center gap-3 ${
        active ? "border-green-300 bg-green-50 dark:bg-green-950/20" : ""
      }`}
    >
      <div>{icon}</div>

      <div className="flex-1">
        <p className="font-medium">{title}</p>

        <p className="text-xs text-muted-foreground">
          {active ? "Verified" : "Not Verified"}
        </p>
      </div>
    </div>
  );
}
